/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Workspaces", description = "Learn how to create, manage, and delete Hightouch workspaces — including what each field on the creation form means, how to switch between workspaces, and common setup mistakes to avoid.", lastUpdatedDate = new Date(1790553600000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Workspaces",
        description: "Learn how to create, manage, and delete Hightouch workspaces — including what each field on the creation form means, how to switch between workspaces, and common setup mistakes to avoid.",
        lastUpdatedDate: "Mon Sep 28 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Who should create workspaces",
          "depth": 0,
          "id": "who-should-create-workspaces"
        }, {
          "value": "First sign-in",
          "depth": 0,
          "id": "first-sign-in"
        }, {
          "value": "New company",
          "depth": 1,
          "id": "new-company"
        }, {
          "value": "Existing company",
          "depth": 1,
          "id": "existing-company"
        }, {
          "value": "Workspace creation form",
          "depth": 0,
          "id": "workspace-creation-form"
        }, {
          "value": "Organization",
          "depth": 1,
          "id": "organization"
        }, {
          "value": "Workspace name",
          "depth": 1,
          "id": "workspace-name"
        }, {
          "value": "Workspace URL/slug",
          "depth": 1,
          "id": "workspace-urlslug"
        }, {
          "value": "Region and cloud provider",
          "depth": 1,
          "id": "region-and-cloud-provider"
        }, {
          "value": "After creation",
          "depth": 0,
          "id": "after-creation"
        }, {
          "value": "Switching between workspaces",
          "depth": 0,
          "id": "switching-between-workspaces"
        }, {
          "value": "Deleting a workspace",
          "depth": 0,
          "id": "deleting-a-workspace"
        }, {
          "value": "Do you need another workspace?",
          "depth": 0,
          "id": "do-you-need-another-workspace"
        }, {
          "value": "Troubleshooting",
          "depth": 0,
          "id": "troubleshooting"
        }, {
          "value": "I was invited but see the create-workspace screen",
          "depth": 1,
          "id": "i-was-invited-but-see-the-create-workspace-screen"
        }, {
          "value": "I accidentally created a duplicate organization",
          "depth": 1,
          "id": "i-accidentally-created-a-duplicate-organization"
        }, {
          "value": "I need to change my workspace region",
          "depth": 1,
          "id": "i-need-to-change-my-workspace-region"
        }, {
          "value": "Related resources",
          "depth": 0,
          "id": "related-resources"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      p: "p",
      em: "em",
      a: "a",
      h2: "h2",
      strong: "strong",
      h3: "h3",
      img: "img",
      ol: "ol",
      li: "li",
      ul: "ul",
      code: "code",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td"
    }, _provideComponents(), props.components), {PylonLink} = _components;
    if (!PylonLink) _missingMdxReference("PylonLink", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.p, {
        children: _jsx(_components.em, {
          children: "When you first sign up for Hightouch, or when your team needs an additional workspace, you'll use the workspace creation flow. This guide walks through when and how to create a workspace, what each field means, and common mistakes to avoid."
        })
      }), "\n", _jsx(Tip, {
        children: _jsxs(_components.p, {
          children: ["Before creating a new workspace, make sure you actually need one. For most\nteams, a single production workspace is sufficient — Hightouch's ", _jsx(_components.a, {
            href: "/workspace-management/governance",
            children: "governance\ncontrols"
          }), ", including user groups, roles,\nSpaces, and approval flows, let multiple teams share a workspace safely. See\n", _jsx(_components.a, {
            href: "#do-you-need-another-workspace",
            children: "Do you need another workspace?"
          }), " below."]
        })
      }), "\n", _jsx(_components.h2, {
        id: "who-should-create-workspaces",
        children: "Who should create workspaces"
      }), "\n", _jsxs(_components.p, {
        children: ["Workspace creation is restricted to users who have ", _jsx(_components.strong, {
          children: "organization admin"
        }), " privileges. In most companies, this is the ", _jsx(_components.a, {
          href: "/getting-started/it",
          children: _jsx(_components.strong, {
            children: "IT or platform admin"
          })
        }), " — typically the person who also owns SSO configuration, billing, and user provisioning. If you're setting up Hightouch for the first time, the person who signs up becomes the first organization admin automatically."]
      }), "\n", _jsx(_components.p, {
        children: "If your organization uses SSO, workspace creation may be further restricted — in that case, contact your IT team or Hightouch administrator."
      }), "\n", _jsx(Tip, {
        children: _jsxs(_components.p, {
          children: ["If you see the message ", _jsx(_components.strong, {
            children: "\"SSO users are not allowed to create workspaces\""
          }), ",\nyour organization has disabled self-serve workspace creation. Reach out to\nyour platform admin to have them create the workspace on your behalf."]
        })
      }), "\n", _jsx(_components.h2, {
        id: "first-sign-in",
        children: "First sign-in"
      }), "\n", _jsx(_components.p, {
        children: "What you see when you first log in to Hightouch depends on whether your company already has an organization set up."
      }), "\n", _jsx(_components.h3, {
        id: "new-company",
        children: "New company"
      }), "\n", _jsxs(_components.p, {
        children: ["If no one at your company has used Hightouch before, sign up at ", _jsx(_components.a, {
          href: "https://app.hightouch.com?from=docs",
          children: "app.hightouch.com"
        }), " using Google, Microsoft, or email."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "workspace-management/creation/sign-up.png",
          alt: "The Hightouch sign-up screen with Google, Microsoft, and email options"
        })
      }), "\n", _jsx(_components.p, {
        children: "After signing up, you'll be prompted to:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Create an organization"
          }), " — this represents your company and is the container for billing, SSO, and user management."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Create your first workspace"
          }), " — using the form described below."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["After creating your workspace, the ", _jsx(_components.strong, {
          children: "Get started"
        }), " checklist guides you through connecting a data source, adding destinations, and inviting teammates."]
      }), "\n", _jsx(_components.h3, {
        id: "existing-company",
        children: "Existing company"
      }), "\n", _jsxs(_components.p, {
        children: ["If your company already has an organization and workspaces in Hightouch, you should ", _jsx(_components.strong, {
          children: "not"
        }), " create a new organization. Instead:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Ask an existing admin to ", _jsx(_components.strong, {
            children: "invite you"
          }), " to the appropriate workspace, or"]
        }), "\n", _jsxs(_components.li, {
          children: ["Log in through your company's ", _jsx(_components.strong, {
            children: "SSO provider"
          }), ", which will route you to the correct organization and workspace automatically."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "workspace-management/creation/login.png",
          alt: "The Hightouch login screen with Google, Microsoft, email, and SSO options"
        })
      }), "\n", _jsx(Warning, {
        children: _jsxs(_components.p, {
          children: ["If you were invited to Hightouch but land on the workspace creation screen\ninstead of your team's workspace, ", _jsx(_components.strong, {
            children: "stop and contact the person who invited\nyou"
          }), ". Creating a new organization or workspace in this situation will result\nin a duplicate that needs to be cleaned up."]
        })
      }), "\n", _jsx(_components.h2, {
        id: "workspace-creation-form",
        children: "Workspace creation form"
      }), "\n", _jsxs(_components.p, {
        children: ["The creation form has four fields: ", _jsx(_components.strong, {
          children: "Organization"
        }), ", ", _jsx(_components.strong, {
          children: "Workspace name"
        }), ", ", _jsx(_components.strong, {
          children: "Workspace slug"
        }), ", and ", _jsx(_components.strong, {
          children: "Region"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "workspace-management/creation/create-workspace-form.png",
          alt: "The workspace creation form with fields for name, slug, region, cloud provider, and organization"
        })
      }), "\n", _jsx(_components.h3, {
        id: "organization",
        children: "Organization"
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.strong, {
          children: "Organization"
        }), " dropdown lets you select which organization the new workspace belongs to."]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "New organization"
          }), " — if you're the first person at your company signing up for Hightouch, select ", _jsx(_components.strong, {
            children: "New organization"
          }), ". You'll provide your organization name (typically your company's legal or common name, such as \"Acme Inc.\"), and Hightouch will create the organization and make you its first organization admin."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Existing organization"
          }), " — if your company already has an organization, select it from the dropdown. You'll only see organizations where you have permission to create workspaces."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["Most companies should have ", _jsx(_components.strong, {
          children: "exactly one"
        }), " organization. Multiple organizations are only appropriate when you operate truly separate legal or identity domains — for example, a holding company with independent subsidiaries that don't share users, billing, or SSO configuration."]
      }), "\n", _jsx(Warning, {
        children: _jsxs(_components.p, {
          children: ["If your company already uses Hightouch, do not select ", _jsx(_components.strong, {
            children: "New organization"
          }), ".\nThis creates a duplicate organization that will need to be cleaned up.\nInstead, ask an existing admin to invite you, or log in via SSO."]
        })
      }), "\n", _jsx(_components.h3, {
        id: "workspace-name",
        children: "Workspace name"
      }), "\n", _jsx(_components.p, {
        children: "The name appears in the workspace switcher and helps your team identify the workspace. Choose something descriptive:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Single workspace:"
          }), " Your company name, or something like ", _jsx(_components.code, {
            children: "<Company> Production"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Production + sandbox:"
          }), " ", _jsx(_components.code, {
            children: "<Company> Production"
          }), " and ", _jsx(_components.code, {
            children: "<Company> Sandbox"
          }), " (or ", _jsx(_components.code, {
            children: "Playground"
          }), ")."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Multiple workspaces:"
          }), " Use a pattern that makes the distinction clear, such as ", _jsx(_components.code, {
            children: "<Company> – US"
          }), " and ", _jsx(_components.code, {
            children: "<Company> – EU"
          }), ", or ", _jsx(_components.code, {
            children: "<Brand A>"
          }), " and ", _jsx(_components.code, {
            children: "<Brand B>"
          }), "."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["You can change the workspace name later in ", _jsx(_components.strong, {
          children: "Settings → Workspace"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "workspace-management/creation/workspace-change-name.png",
          alt: "Workspace settings showing the workspace name, slug, billing organization, and region fields"
        })
      }), "\n", _jsx(_components.h3, {
        id: "workspace-urlslug",
        children: "Workspace URL/slug"
      }), "\n", _jsxs(_components.p, {
        children: ["The slug becomes part of your workspace URL: ", _jsx(_components.code, {
          children: "app.hightouch.com/<slug>"
        }), ". It must be globally unique and is auto-generated from the workspace name."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "workspace-management/creation/workspace-slug-url.png",
          alt: "The workspace slug shown in the browser URL bar"
        })
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["Treat the slug as ", _jsx(_components.strong, {
            children: "permanent"
          }), ". While workspace admins can technically update\nit later in ", _jsx(_components.strong, {
            children: "Settings → Workspace"
          }), ", changing the slug breaks all existing\nbookmarked URLs, saved links, scripts, and integrations that reference the old\nslug — and there is no automatic redirect. Choose carefully before submitting\nthe form."]
        })
      }), "\n", _jsx(_components.h3, {
        id: "region-and-cloud-provider",
        children: "Region and cloud provider"
      }), "\n", _jsxs(_components.p, {
        children: ["The region determines where Hightouch processes your data. You'll select both a ", _jsx(_components.strong, {
          children: "geographic region"
        }), " (United States, European Union, Asia Pacific, or Middle East) and a ", _jsx(_components.strong, {
          children: "cloud provider"
        }), " (AWS, Google Cloud, or Azure). The available cloud providers vary by region."]
      }), "\n", _jsx(_components.p, {
        children: "When choosing a region:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Prefer the region closest to your primary data warehouse"
          }), " and your end users to reduce latency."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Consider data residency requirements."
          }), " If compliance mandates that data stay within a specific jurisdiction, select a region in that jurisdiction."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Check feature availability."
          }), " Not all features are available in every region. Refer to the ", _jsx(_components.a, {
            href: "/security/regions",
            children: "Regions"
          }), " documentation for a current compatibility matrix."]
        }), "\n"]
      }), "\n", _jsx(Warning, {
        children: _jsxs(_components.p, {
          children: ["Region cannot be changed after workspace creation. If you create a workspace\nin the wrong region, you'll need to create a new workspace in the correct\nregion and migrate your configuration.", " ", "\n", _jsx(PylonLink, {
            pylonText: "Hello, I created a workspace in the wrong region and need help migrating.",
            linkText: "Contact support"
          }), " ", "\nif you need assistance."]
        })
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["Some newer regions may be in limited availability. If the region you need\nisn't visible in the dropdown,", " ", "\n", _jsx(PylonLink, {
            pylonText: "Hello, I need to create a workspace in a region that isn't listed in the dropdown.",
            linkText: "contact Hightouch"
          }), " ", "\nto check availability and have our team help provision your workspace."]
        })
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "workspace-management/creation/create-workspace-form-filled.png",
          alt: "A completed workspace creation form with name, slug, region, and cloud provider selected"
        })
      }), "\n", _jsx(_components.h2, {
        id: "after-creation",
        children: "After creation"
      }), "\n", _jsxs(_components.p, {
        children: ["Once your workspace is created, Hightouch presents a ", _jsx(_components.strong, {
          children: "Get started"
        }), " checklist that walks you through initial setup:"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "workspace-management/creation/get-started-checklist.png",
          alt: "The Get started checklist shown after workspace creation"
        })
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Invite teammates"
          }), " — add collaborators so they can help configure the workspace."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Set up storage bucket"
          }), " ", _jsx(_components.em, {
            children: "(Business tier only)"
          }), " — bring your own S3, GCS, or Azure Blob Storage bucket for data-at-rest. This step only appears for Business tier workspaces. See ", _jsx(_components.a, {
            href: "/security/storage",
            children: "Storage"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Connect to your data warehouse"
          }), " — set up access to your data source. See ", _jsx(_components.a, {
            href: "/sources/overview",
            children: "Sources"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Add destinations"
          }), " — connect your marketing, sales, or other downstream tools. See ", _jsx(_components.a, {
            href: "/destinations/overview",
            children: "Destinations"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(Info, {
        children: _jsx(_components.p, {
          children: "The exact steps shown may vary depending on your plan tier and workspace\nconfiguration."
        })
      }), "\n", _jsx(_components.h2, {
        id: "switching-between-workspaces",
        children: "Switching between workspaces"
      }), "\n", _jsx(_components.p, {
        children: "If you have access to multiple workspaces, you can switch between them in two ways:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Workspace switcher"
          }), " — click the workspace name in the top-left corner of the sidebar to open the workspace dropdown, or press ", _jsx(_components.strong, {
            children: "S"
          }), " then ", _jsx(_components.strong, {
            children: "W"
          }), " anywhere in the app. You can also open the command bar with ", _jsx(_components.strong, {
            children: "Cmd+K"
          }), " (Mac) / ", _jsx(_components.strong, {
            children: "Ctrl+K"
          }), " (Windows) and type \"Switch workspace.\" Search by name or slug and select the workspace you want."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "workspace-management/creation/workspace-switcher-dropdown.png",
          alt: "The workspace switcher dropdown in the sidebar"
        })
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Direct URL"
          }), " — navigate to a URL containing another workspace's slug (for example, ", _jsx(_components.code, {
            children: "app.hightouch.com/other-workspace/syncs"
          }), "). Hightouch will automatically switch your active workspace to match."]
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "deleting-a-workspace",
        children: "Deleting a workspace"
      }), "\n", _jsxs(_components.p, {
        children: ["There is no self-serve option to delete a workspace. You can find the deletion option in ", _jsx(_components.strong, {
          children: "Settings → Workspace"
        }), ", but it routes you to Hightouch support."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "workspace-management/creation/workspace-delete.png",
          alt: "The Delete workspace section in Workspace settings"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["If you need to remove a workspace — for example, one created by mistake — ", _jsx(PylonLink, {
          pylonText: "Hello, I need to delete a workspace that was created in error.",
          linkText: "contact Hightouch support"
        }), ". Before submitting your request, remove or disconnect all sources, destinations, syncs, and other resources from the workspace. All current organization or workspace admins will need to approve the deletion."]
      }), "\n", _jsx(_components.h2, {
        id: "do-you-need-another-workspace",
        children: "Do you need another workspace?"
      }), "\n", _jsxs(_components.p, {
        children: ["Before creating an additional workspace, consider whether ", _jsx(_components.a, {
          href: "/workspace-management/environments",
          children: "Environments"
        }), " or other governance controls would be a better fit."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.strong, {
          children: "Use Environments when:"
        })
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "You need dev / staging / production promotion for the same data and teams."
        }), "\n", _jsx(_components.li, {
          children: "You want change control and approval flows without hard security isolation."
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.strong, {
          children: "Create a separate workspace when:"
        })
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "You have strict isolation requirements — regulatory, regional, or business-unit-level — where sources, destinations, and data must not be visible across boundaries."
        }), "\n", _jsx(_components.li, {
          children: "You need fully separate tenants under the same commercial relationship, such as independent subsidiaries."
        }), "\n"]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              align: "left",
              children: "Consideration"
            }), _jsx(_components.th, {
              align: "left",
              children: "Environments"
            }), _jsx(_components.th, {
              align: "left",
              children: "Separate workspaces"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: "Shared sources and destinations"
            }), _jsx(_components.td, {
              align: "left",
              children: "Yes"
            }), _jsx(_components.td, {
              align: "left",
              children: "No — each workspace has its own"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: "Shared user roster"
            }), _jsx(_components.td, {
              align: "left",
              children: "Yes"
            }), _jsx(_components.td, {
              align: "left",
              children: "No — managed separately per workspace"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: "Approval flows"
            }), _jsx(_components.td, {
              align: "left",
              children: "Yes"
            }), _jsx(_components.td, {
              align: "left",
              children: "Yes, but independent per workspace"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: "Data isolation"
            }), _jsx(_components.td, {
              align: "left",
              children: "Logical (same workspace)"
            }), _jsx(_components.td, {
              align: "left",
              children: "Physical (nothing shared)"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: "Typical use case"
            }), _jsx(_components.td, {
              align: "left",
              children: "Dev → Staging → Prod"
            }), _jsx(_components.td, {
              align: "left",
              children: "US region vs EU region"
            })]
          })]
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["For more on structuring your organization, see ", _jsx(_components.a, {
          href: "/workspace-management/governance",
          children: "Governance best practices"
        }), "."]
      }), "\n", _jsx(_components.h2, {
        id: "troubleshooting",
        children: "Troubleshooting"
      }), "\n", _jsx(_components.h3, {
        id: "i-was-invited-but-see-the-create-workspace-screen",
        children: "I was invited but see the create-workspace screen"
      }), "\n", _jsx(_components.p, {
        children: "This usually means the invitation didn't link you to the correct organization or workspace. Don't create a new organization — instead, contact the person who invited you and ask them to re-send the invite or check that your account is correctly associated with their organization."
      }), "\n", _jsx(_components.h3, {
        id: "i-accidentally-created-a-duplicate-organization",
        children: "I accidentally created a duplicate organization"
      }), "\n", _jsxs(_components.p, {
        children: ["If you signed up directly instead of using an invitation or SSO, you may have created a second organization for your company. ", _jsx(PylonLink, {
          pylonText: "Hello, I accidentally created a duplicate organization and need help merging or removing it.",
          linkText: "Contact support"
        }), " to consolidate your organizations."]
      }), "\n", _jsx(_components.h3, {
        id: "i-need-to-change-my-workspace-region",
        children: "I need to change my workspace region"
      }), "\n", _jsxs(_components.p, {
        children: ["Region cannot be changed self-serve after creation. ", _jsx(PylonLink, {
          pylonText: "Hello, I need to migrate my workspace to a different region.",
          linkText: "Contact Hightouch support"
        }), " to plan a migration. Before you reach out, be aware of the following:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Brief outage required."
          }), " The migration involves a short period of downtime for the affected workspace. Schedule it during low-traffic hours."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Tunnels must be recreated."
          }), " Existing ", _jsx(_components.a, {
            href: "/security/ssh-tunneling",
            children: "SSH tunnels"
          }), " cannot be migrated between regions. You'll need to set up new tunnels in the target region after the migration."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "IP allowlists need updating."
          }), " If your sources or destinations are behind a firewall that allowlists Hightouch's ", _jsx(_components.a, {
            href: "/security/networking#ip-addresses",
            children: "IP addresses"
          }), ", add the new region's IP range before the migration and remove the old range afterward."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Sync history and debugger data."
          }), " If you use ", _jsx(_components.a, {
            href: "/security/storage#self-hosted-storage",
            children: "self-hosted storage"
          }), " or the ", _jsx(_components.a, {
            href: "/syncs/lightning-sync-engine",
            children: "Lightning sync engine"
          }), ", your sync history and CDC state are preserved. If you use Hightouch-managed storage with the standard sync engine, syncs will need a full resync after the migration and pre-migration debugger data may not carry over."]
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "related-resources",
        children: "Related resources"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/workspace-management/overview",
            children: "Core concepts"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/workspace-management/governance",
            children: "Governance best practices"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/workspace-management/environments",
            children: "Environments"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/security/regions",
            children: "Regions"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/security/storage",
            children: "Storage"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/workspace-management/user-management",
            children: "Managing users and groups"
          })
        }), "\n"]
      })]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
